import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { isImpersonating, exitImpersonation } from "../../lib/impersonation";
import KIcon from "./KIcon";
import { CabinetLocaleProvider, useCabinetLang } from "./i18n";
import { setCabinetMoney } from "./money";
import PrefSwitcher from "./PrefSwitcher";
import "./cabinet.css";

/** Count of everything that waits on the client — the badge on «Мої запити». */
export const countNeedsYou = (p) => {
  if (!p) return 0;
  const pays = (p.payments || []).filter((x) => x.state === "overdue" || x.state === "invoiced").length;
  const reqs = (p.action_required || []).filter((r) => r.request_type !== "payment_request").length;
  const brief = (p.setup_tasks || []).filter((t) => t.setup_stage === "client").length;
  return pays + reqs + brief + (p.tasks_awaiting_approval || []).length + (p.task_change_requests || []).length;
};

/**
 * Client cabinet shell — 1:1 with the approved prototype: left sidebar
 * (logo, client card, Огляд · Задачі · Оплати · Мої запити, Вийти), dark only.
 * Replaces the internal SidebarLayout for the client role.
 */
const ClientShell = ({ children, name }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [needs, setNeeds] = useState(0);
  // The cabinet speaks the client's language. One fetch already happens here
  // for the badge, so the locale rides along and every view below gets it
  // through the provider. backlog 87c81e60 / 6f47ee8f.
  //
  // The pages below are `children`, an element built by SidebarLayout. When
  // only this component re-renders, React reuses that element as-is, so a page
  // that formats text outside the context (tr(), date and money helpers) would
  // keep the language of its first render. So the language is settled BEFORE
  // the pages mount: null = still asking. The session cache, keyed by the
  // client being viewed, keeps later navigations instant.
  const asClient = new URLSearchParams(location.search).get("as_client");
  const cacheKey = `cabinet_lang:${asClient || "self"}`;
  const cached = (() => { try { return sessionStorage.getItem(cacheKey); } catch (_) { return null; } })();
  const [lang, setLang] = useState(cached === "en" || cached === "uk" ? cached : null);
  // Display currency is the viewer's choice and nobody else's business, so it
  // lives in the browser. "contract" = the exact per-currency amounts.
  const ccyKey = `cabinet_ccy:${asClient || "self"}`;
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem(ccyKey) || "contract"; } catch (_) { return "contract"; }
  });
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/currency-rates/current")
      .then((r) => setRates(r.data))
      .catch(() => setRates(null));  // no rates → the switcher stays on exact amounts
  }, []);

  // Before children render, same reason as the language provider.
  setCabinetMoney(currency, rates);

  const pickLang = (code) => {
    setLang(code);
    try { localStorage.setItem(`cabinet_lang_pref:${asClient || "self"}`, code); } catch (_) { /* private mode */ }
  };
  const pickCurrency = (code) => {
    setCurrency(code);
    try { localStorage.setItem(ccyKey, code); } catch (_) { /* private mode */ }
  };

  useEffect(() => {
    api.get(asClient ? `/client/portal?as_client=${encodeURIComponent(asClient)}` : "/client/portal")
      .then((r) => {
        setNeeds(countNeedsYou(r.data));
        let l = r.data?.locale === "en" ? "en" : "uk";
        // A language the client picked in the cabinet wins over the card: the
        // card sets the default, the switcher sets theirs. backlog b3f87131.
        try {
          const chosen = localStorage.getItem(`cabinet_lang_pref:${asClient || "self"}`);
          if (chosen === "en" || chosen === "uk") l = chosen;
        } catch (_) { /* private mode */ }
        setLang(l);
        try { sessionStorage.setItem(cacheKey, l); } catch (_) { /* private mode */ }
      })
      .catch(() => { setNeeds(0); setLang((v) => v || "uk"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, asClient]);

  const path = location.pathname;
  const onDash = path === "/client" || path === "/client-preview";
  const dashPath = path === "/client-preview" ? `/client-preview${location.search}` : "/client";
  const go = (to, hash) => (e) => {
    e.preventDefault();
    if (hash) {
      if (onDash) {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(dashPath);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
      }
      return;
    }
    navigate(to);
  };
  const isOn = (key) => {
    if (key === "dash") return onDash || path.startsWith("/client/projects");
    if (key === "pay") return path.startsWith("/client/payments");
    if (key === "tasks") return path.startsWith("/client/tasks");
    if (key === "req") return path.startsWith("/client/requests");
    return false;
  };
  const leave = () => {
    if (isImpersonating()) {
      exitImpersonation();
      window.close();
      setTimeout(() => { window.location.href = "/login"; }, 100);
      return;
    }
    logout();
  };

  return (
    <CabinetLocaleProvider lang={lang || "uk"}>
      <ShellBody
        name={name}
        lang={lang}
        currency={currency}
        onLang={pickLang}
        onCurrency={pickCurrency}
        needs={needs}
        isOn={isOn}
        go={go}
        dashPath={dashPath}
        leave={leave}
      >
        {lang ? children : null}
      </ShellBody>
    </CabinetLocaleProvider>
  );
};

const ShellBody = ({ name, needs, isOn, go, dashPath, leave, children, lang, currency, onLang, onCurrency }) => {
  const { t } = useCabinetLang();
  return (
    <div className="kc">
      {isImpersonating() && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, background: "#d97706", color: "#000", fontSize: 12, fontWeight: 500, padding: "6px 16px" }}>
          <span>{t("Перегляд як клієнт")}{name ? `: ${name}` : ""} · {t("режим тільки для перегляду (read-only)")}</span>
          <button type="button" onClick={leave} style={{ textDecoration: "underline", color: "#000" }}>{t("Вийти")}</button>
        </div>
      )}
      <div className="app v-b">
        <aside className="side">
          <div className="logo"><span className="logo-mark" /><span>SMART ORANGE HUB</span></div>
          <div className="client">
            <div className="ava">{(name || t("К")).trim().charAt(0).toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <b style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || t("Клієнт")}</b>
              <span>{t("Клієнт")}</span>
            </div>
          </div>
          <nav className="nav" aria-label={t("Розділи")}>
            <a href="/client" className={isOn("dash") ? "on" : ""} onClick={go(dashPath)}><KIcon name="home" />{t("Огляд")}</a>
            <a href="/client/tasks" className={isOn("tasks") ? "on" : ""} onClick={go("/client/tasks")}><KIcon name="tasks" />{t("Задачі")}</a>
            <a href="/client/payments" className={isOn("pay") ? "on" : ""} onClick={go("/client/payments")}><KIcon name="wallet" />{t("Оплати")}</a>
            <a href="/client/requests" className={isOn("req") ? "on" : ""} onClick={go("/client/requests")}>
              <KIcon name="inbox" />{t("Мої запити")}{needs > 0 && <span className="badge">{needs}</span>}
            </a>
          </nav>
          <div className="side-foot nav">
            <a href="/login" onClick={(e) => { e.preventDefault(); leave(); }}><KIcon name="logout" />{t("Вийти")}</a>
          </div>
        </aside>
        <div style={{ minWidth: 0 }}>
          <PrefSwitcher lang={lang} onLang={onLang} currency={currency} onCurrency={onCurrency} />
          {/* Keyed on the preferences: the pages are `children`, an element the
              layout above built, so a re-render here does not reach them. Money
              and dates are formatted by plain functions that read the ambient
              preference, so the subtree has to be rebuilt for a switch to take
              effect at all. backlog b3f87131 / ef0c7e7f. */}
          <div key={`${lang}:${currency}`}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default ClientShell;
